/* eslint-disable default-param-last */
import React, {
  createContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import AlertModal from '../components/AlertModal';
import Notification from '../components/Notification';
import api from '../services/api';

export const ManagerContext = createContext();

function ManagerProvider({ children }) {
  const notificationRef = useRef();
  const confirmRef = useRef();
  const [currentUrl, setCurrentUrl] = useState('');
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [totalPages, setTotalPages] = useState(0);

  const handlerError = useCallback((error, title) => {
    if (typeof error === 'string') {
      notificationRef.current.notify({
        title,
        message: error,
        color: 'danger',
      });
      return;
    }

    if (error && error.response) {
      const { message } = error.response.data;
      if (message) {
        notificationRef.current.notify({
          title,
          message,
          color: 'danger',
        });
        return;
      }
    }

    notificationRef.current.notify({
      message: title,
      color: 'danger',
    });
  }, []);

  const load = useCallback(
    (url, page = 1, searchArray = [], limit = 10, onSuccess, onError) => {
      setDatas([]);
      if (url) {
        setCurrentUrl(url);
        (async () => {
          try {
            setLoading(true);
            // console.log('limit', limit);
            let urlString = `${url}?page=${page}&limit=${limit}`;
            if (searchArray.length) {
              urlString = `${urlString}&search=${JSON.stringify(searchArray)}`;
            }
            const res = await api.get(urlString);
            // console.log(res.data);
            setDatas(res.data.docs);
            setHasPrevPage(res.data.hasPrevPage);
            setHasNextPage(res.data.hasNextPage);
            setTotalPages(res.data.totalPages);
            if (onSuccess) {
              onSuccess();
            }
            setLoading(false);
          } catch (error) {
            if (onError) {
              onError(error);
            }
            setLoading(false);
            handlerError(error, 'Falha ao carregar os dados');
          }
        })();
      }
    },
    [handlerError],
  );

  const store = useCallback(
    (url, data, onSuccess, onError, addDatas = true) => {
      if (url && data) {
        (async () => {
          try {
            setLoading(true);
            const res = await api.post(url, data);
            if (addDatas) {
              setDatas((d) => [res.data, ...d]);
            }
            notificationRef.current.notify({
              message: res.data.message || 'Cadastrado com sucesso!!!',
            });
            if (onSuccess) {
              onSuccess(res.data);
            }
            setLoading(false);
          } catch (error) {
            setDatas([]);
            if (onError) {
              onError(error);
            }
            setLoading(false);
            handlerError(error, 'Falha ao Cadastrar');
          }
        })();
      }
    },
    [handlerError],
  );

  const update = useCallback(
    (id, url, data, onSuccess, onError, addDatas = true) => {
      if (id && url && data) {
        (async () => {
          try {
            setLoading(true);
            const res = await api.put(`${url}/${id}`, data);
            if (addDatas) {
              setDatas((d) => [
                res.data,
                ...d.filter((value) => (value.id || value._id) !== id),
              ]);
            }
            notificationRef.current.notify({
              message: res.data.message || 'Alterado com sucesso!!!',
            });
            if (onSuccess) {
              onSuccess(res.data);
            }
            setLoading(false);
          } catch (error) {
            if (onError) {
              onError(error);
            }
            setLoading(false);
            handlerError(error, 'Falha ao alterar');
          }
        })();
      }
    },
    [handlerError],
  );

  const destroy = useCallback(
    (id, url, onSuccess, onError) => {
      if (id && url) {
        (async () => {
          try {
            // console.log('destroy', id, url);
            confirmRef.current.show({
              message: 'Tem certeza que deseja excluir?',
              confirmColor: 'danger',
              confirmText: 'Excluir',
              cancelText: 'Cancelar',
              cancelColor: 'link',
              onConfirm: async () => {
                try {
                  setLoading(true);
                  await api.delete(`${url}/${id}`);
                  setDatas((d) => d.filter((value) => (value.id || value._id) !== id));
                  notificationRef.current.notify({
                    message: 'Excluido com sucesso',
                  });
                  if (onSuccess) {
                    onSuccess();
                  }
                  confirmRef.current.close();
                  setLoading(false);
                } catch (error) {
                  if (onError) {
                    onError(error);
                  }
                  setLoading(false);
                  handlerError(error, 'Falha ao excluir');
                }
              },
            });
          } catch (error) {
            if (onError) {
              onError(error);
            }
            setLoading(false);
            handlerError(error, 'Falha ao excluir');
          }
        })();
      }
    },
    [handlerError],
  );

  const value = useMemo(
    () => ({
      currentUrl,
      loading,
      load,
      datas,
      setDatas,
      hasNextPage,
      hasPrevPage,
      totalPages,
      store,
      update,
      destroy,
    }),
    [
      currentUrl,
      loading,
      load,
      datas,
      setDatas,
      hasNextPage,
      hasPrevPage,
      totalPages,
      store,
      update,
      destroy,
    ],
  );

  return (
    <ManagerContext.Provider value={value}>
      <AlertModal ref={confirmRef} />
      <Notification ref={notificationRef} />
      {children}
    </ManagerContext.Provider>
  );
}

export default ManagerProvider;
